<template>
  <!-- GRID VIEW -->
  <MCard v-if="viewMode === 'grid'" class="product-card h-100" hover>
    <component :is="'script'" type="application/ld+json" v-text="productJsonLd" />

    <div class="product-card__image-wrapper">
      <NuxtLink :to="productUrl" class="product-card__image-link">
        <MImg :src="cardImageUrl" :alt="product.name" :height="200" :proxy-width="600" contain class="product-card__image" @error="onImgError" />
      </NuxtLink>
      <ClientOnly>
        <MBtn icon size="small" variant="flat" color="white" class="product-card__wishlist" :class="{ 'product-card__wishlist--active': isWishlisted }" :aria-label="isWishlisted ? 'Von Merkliste entfernen' : 'Auf Merkliste setzen'" @click.prevent="toggleWishlist">
          <MIcon :icon="isWishlisted ? 'heart' : 'heart-outline'" :color="isWishlisted ? 'var(--m-secondary)' : 'var(--m-on-surface-variant)'" :size="20" />
        </MBtn>
        <template #fallback>
          <MBtn icon size="small" variant="flat" color="white" class="product-card__wishlist" aria-label="Auf Merkliste setzen">
            <MIcon icon="heart-outline" color="var(--m-on-surface-variant)" :size="20" />
          </MBtn>
        </template>
      </ClientOnly>
      <MChip v-if="discountPercent" color="secondary" size="small" variant="flat" class="product-card__badge">
        -{{ discountPercent }}%
      </MChip>
    </div>

    <MCardText class="product-card__content px-3 pa-0">
      <h3 class="product-card__title">{{ cleanTitle(product.name) }}</h3>
    </MCardText>

    <MCardText class="product-card__bottom pa-0 px-3 pb-2">
      <div class="product-card__price-row">
        <div class="product-card__left-info">
          <span v-if="product.merchantName" class="product-card__merchant">{{ product.merchantName }}</span>
          <span v-if="deliveryInfo" class="product-card__delivery">{{ deliveryInfo }}</span>
        </div>
        <div class="product-card__prices">
          <span v-if="hasOriginalPrice" class="product-card__original-price">
            {{ formatPrice(product.originalPrice!) }}
          </span>
          <span class="product-card__price">{{ formatPrice(product.price) }}</span>
        </div>
      </div>
      <!-- Footer: Details links, Angebote rechts -->
      <div class="product-card__footer">
        <NuxtLink :to="productUrl" class="product-card__details" @click.stop>
          <MIcon icon="information-outline" :size="14" class="mr-1" />Details
        </NuxtLink>
        <span v-if="product._count?.variants" class="product-card__offers">
          {{ product._count.variants + 1 }} Varianten
        </span>
        <span v-else class="product-card__offers">
          1 Angebot
        </span>
      </div>
    </MCardText>
  </MCard>

  <!-- LIST VIEW — Preisvergleich-Zeile -->
  <div v-else class="pv-row">
    <component :is="'script'" type="application/ld+json" v-text="productJsonLd" />

    <div class="pv-row__image">
      <NuxtLink :to="productUrl">
        <MImg :src="cardImageUrl" :alt="product.name" :height="72" :width="72" :proxy-width="160" contain @error="onImgError" />
      </NuxtLink>
    </div>

    <div class="pv-row__name">
      <NuxtLink :to="productUrl" class="pv-row__title">
        {{ product.name }}
      </NuxtLink>
      <span v-if="product.brand" class="pv-row__brand">{{ product.brand }}</span>
      <MChip v-if="product._count?.variants && product._count.variants <= 50" size="x-small" variant="tonal" color="primary">
        {{ product._count.variants + 1 }} Varianten
      </MChip>
    </div>

    <div class="pv-row__price">
      <span class="pv-row__price-main">{{ formatPrice(product.price) }}</span>
      <span v-if="hasOriginalPrice" class="pv-row__price-old">{{ formatPrice(product.originalPrice!) }}</span>
      <span v-if="deliveryCost !== undefined" class="pv-row__delivery-cost">
        <template v-if="deliveryCost === 0">inkl. Versand</template>
        <template v-else>{{ formatPrice(product.price + deliveryCost) }} inkl. Versand</template>
      </span>
      <span v-if="deliveryCost !== undefined" class="pv-row__shipping-info">
        <template v-if="deliveryCost === 0">
          <MIcon :size="12" icon="check-circle" color="success" />
          <span class="pv-row__shipping--free">Kostenloser Versand</span>
        </template>
        <template v-else>zzgl. {{ formatPrice(deliveryCost) }} Versand</template>
      </span>
    </div>

    <div class="pv-row__shop">
      <span v-if="product.merchantName" class="pv-row__merchant">{{ product.merchantName }}</span>
    </div>

    <div class="pv-row__actions">
      <a :href="product.affiliateUrl" target="_blank" rel="noopener sponsored" class="pv-row__cta" @click.stop>
        Zum Shop
        <MIcon :size="14" icon="open-in-new" />
      </a>
      <ClientOnly>
        <button class="pv-row__wishlist-btn" :aria-label="isWishlisted ? 'Von Merkliste entfernen' : 'Auf Merkliste setzen'" @click.prevent="toggleWishlist">
          <MIcon :icon="isWishlisted ? 'heart' : 'heart-outline'" :size="18" :color="isWishlisted ? 'var(--m-secondary)' : 'var(--m-on-surface-variant)'" />
        </button>
      </ClientOnly>
    </div>

    <MChip v-if="discountPercent" color="secondary" size="x-small" variant="flat" class="pv-row__discount">
      -{{ discountPercent }}%
    </MChip>
  </div>
</template>

<script setup lang="ts">
import { useWishlistStore } from "~/stores/wishlist";
import { parseDeliveryCost } from "~/utils/deliveryCost";
import { priceValidUntil } from "~/utils/seo";
import { getProductUrl } from "~/composables/useProductUrl";

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number | null;
  image?: string | null;
  images?: string[];
  affiliateUrl?: string;
  merchantName?: string;
  ean?: string | null;
  brand?: string | null;
  inStock?: boolean;
  slug?: string | null;
  freeShipping?: boolean;
  shippingCost?: number;
  priceTrend?: "up" | "down" | "stable";
  deliveryCost?: number | string;
  metadata?: { deliveryCost?: string; deliveryTime?: string; [key: string]: any };
  _count?: { variants: number };
  category?: {
    slug: string;
    parent?: { slug: string; parent?: { slug: string } | null } | null;
  } | null;
}

const props = withDefaults(defineProps<{ product: Product; viewMode?: 'grid' | 'list' }>(), { viewMode: 'grid' });
const wishlistStore = useWishlistStore();

const productUrl = computed(() => getProductUrl(props.product));
const cardImageUrl = computed(() => props.product.image || props.product.images?.[0] || "/placeholder.svg");

function cleanTitle(name: string): string {
  return name
    .replace(/["""„‟❝❞]/g, "")
    .replace(/\s*[,;|]\s*$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}
const isWishlisted = computed(() => wishlistStore.isInWishlist(props.product.id));
const deliveryCost = computed(() => {
  if (props.product.shippingCost !== undefined) return props.product.shippingCost;
  return parseDeliveryCost(props.product.deliveryCost ?? props.product.metadata?.deliveryCost);
});
const hasOriginalPrice = computed(() => !!(props.product.originalPrice && props.product.originalPrice > props.product.price));

const isFreeShipping = computed(() => props.product.freeShipping || deliveryCost.value === 0);

/**
 * Delivery-Info für unter den Preis. Priorität:
 *  1. Kostenloser Versand → "kostenloser Versand"
 *  2. Konkreter Versandpreis → "+ X,XX € Versand"
 *  3. Lieferzeit aus metadata (z.B. "2-3 Tage" aus Awin-Feed) → "Lieferung: 2-3 Tage"
 *  4. Sonst null (nicht anzeigen)
 */
const deliveryInfo = computed<string | null>(() => {
  if (isFreeShipping.value) return "kostenloser Versand";
  const cost = deliveryCost.value;
  if (cost !== undefined && cost > 0) return `+ ${formatPriceFn(cost)} Versand`;
  const time = props.product.metadata?.deliveryTime;
  if (typeof time === "string" && time.trim().length > 0) {
    const trimmed = time.trim();
    // Manche Feeds liefern bereits "Lieferung in …"/"Lieferbar …" — dann kein
    // doppeltes "Lieferung:" voranstellen.
    return /^liefer/i.test(trimmed) ? trimmed : `Lieferung: ${trimmed}`;
  }
  return null;
});

function formatPriceFn(p: number) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(p);
}

const discountPercent = computed(() => {
  if (props.product.originalPrice && props.product.originalPrice > props.product.price) {
    return Math.round((1 - props.product.price / props.product.originalPrice) * 100);
  }
  return 0;
});

const toggleWishlist = () => {
  wishlistStore.toggleItem({
    productId: props.product.id,
    name: props.product.name,
    price: props.product.price,
    image: props.product.image || undefined,
    affiliateUrl: props.product.affiliateUrl ?? "",
    merchantName: props.product.merchantName,
    ean: props.product.ean ?? undefined,
  });
};

const formatPrice = (price: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(price);

const onImgError = (e: Event) => {
  const img = (e.target as HTMLElement)?.querySelector?.('img') || e.target as HTMLImageElement;
  if (img && img.src !== '/placeholder.svg') img.src = '/placeholder.svg';
};

const productJsonLd = computed(() =>
  JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Product",
    name: props.product.brand ? `${props.product.brand} ${props.product.name}` : props.product.name,
    image: props.product.image || undefined,
    brand: props.product.brand ? { "@type": "Brand", name: props.product.brand } : undefined,
    gtin13: props.product.ean || undefined,
    sku: props.product.id,
    offers: {
      "@type": "Offer",
      price: props.product.price,
      priceCurrency: "EUR",
      priceValidUntil: priceValidUntil(),
      itemCondition: "https://schema.org/NewCondition",
      availability: props.product.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      url: props.product.affiliateUrl,
      seller: props.product.merchantName ? { "@type": "Organization", name: props.product.merchantName } : undefined,
    },
  }),
);
</script>

<style scoped>
/* ========== GRID VIEW ========== */
.product-card { transition: border-color var(--m-transition), box-shadow var(--m-transition); border: 1px solid transparent; display: flex; flex-direction: column; }
@media (hover: hover) {
  .product-card:hover { border-color: var(--m-border); box-shadow: var(--m-shadow); }
  .product-card:hover .product-card__image { transform: scale(1.03); }
}
.product-card__image-wrapper { position: relative; overflow: hidden; background: var(--m-surface); aspect-ratio: 4 / 3; }
.product-card__image-link { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; overflow: hidden; }
:deep(.product-card__image) { transition: transform var(--m-transition-slow); width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
:deep(.product-card__image img) { object-fit: contain !important; width: 100% !important; height: 100% !important; margin: auto; }
.product-card__wishlist { position: absolute; top: 8px; right: 8px; z-index: 1; opacity: 0.6; transition: opacity 0.2s ease; }
.product-card__wishlist--active { opacity: 1; }
@media (hover: hover) { .product-card:hover .product-card__wishlist { opacity: 1; } }
@media (hover: none) { .product-card__wishlist { opacity: 0.85; } }
.product-card__badge { position: absolute; top: 8px; left: 8px; z-index: 1; }
.product-card__tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }
.product-card__tag { font-size: 0.6875rem; line-height: 1; }
.product-card__tag--shipping { color: var(--m-success); font-weight: 500; }
.product-card__tag--stock { color: var(--m-on-surface-variant); }
.product-card__content { padding-bottom: 0; }
.product-card__title { font-family: "Play", sans-serif; font-size: 0.875rem; font-weight: 400; line-height: 1.2; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; color: var(--m-on-surface); height: calc(2 * 1.2em); margin: 4px 0 2px; }
.product-card__bottom { margin-top: auto; padding-top: 0 !important; padding-bottom: 6px !important; }
.product-card__content { padding-top: 0 !important; padding-bottom: 0 !important; }
.product-card__price-row { display: flex; justify-content: space-between; align-items: flex-end; }
.product-card__left-info { display: flex; flex-direction: column; gap: 1px; }
.product-card__prices { display: flex; flex-direction: column; align-items: flex-end; }
.product-card__merchant { font-size: 0.75rem; color: var(--m-on-surface-variant); white-space: nowrap; }
.product-card__original-price { font-family: "Justtellmewhat", sans-serif; font-size: 0.8125rem; font-weight: 700; font-style: italic; color: var(--m-secondary); text-decoration: line-through; line-height: 1; }
.product-card__price { font-family: "Justtellmewhat", sans-serif; font-size: 1rem; font-weight: 700; font-style: italic; color: var(--m-primary); line-height: 1; }
.product-card__shipping { font-size: 0.6875rem; color: var(--m-on-surface-variant); white-space: nowrap; }
.product-card__shipping--free { color: var(--m-success); font-weight: 600; font-size: 0.6875rem; }
.product-card__delivery {
  font-size: 0.6875rem;
  color: var(--m-on-surface-variant);
  line-height: 1.2;
  margin-top: 2px;
  white-space: nowrap;
  text-align: left;
}

.product-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid var(--m-border-light);
}

.product-card__offers {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
}

.product-card__details {
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
  text-decoration: none;
  display: flex;
  align-items: center;
}

.product-card__details:hover,
.product-card__details:focus-visible {
  color: var(--m-primary);
}

/* ========== LIST VIEW — Preisvergleich ========== */
.pv-row {
  display: grid;
  grid-template-columns: 72px 1fr 160px 120px auto;
  align-items: center;
  gap: var(--m-space-3);
  padding: var(--m-space-2) var(--m-space-3);
  border-bottom: 1px solid var(--m-border);
  background: var(--m-surface);
  position: relative;
  transition: background 0.15s ease;
}
.pv-row:hover { background: var(--m-surface-variant); }

.pv-row__image { flex-shrink: 0; width: 72px; height: 72px; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.pv-row__image :deep(img) { object-fit: contain; width: 100%; height: 100%; }

.pv-row__name { min-width: 0; display: flex; flex-direction: column; gap: 2px; align-items: flex-start; }
.pv-row__title {
  font-family: "Play", sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--m-on-surface);
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
}
.pv-row__title:hover { color: var(--m-primary); }
.pv-row__brand { font-size: 0.75rem; color: var(--m-on-surface-variant); }

.pv-row__price { display: flex; flex-direction: column; gap: 2px; }
.pv-row__price-main {
  font-family: "Justtellmewhat", sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  font-style: italic;
  color: var(--m-primary);
  line-height: 1;
}
.pv-row__price-old {
  font-size: 0.8125rem;
  color: var(--m-secondary);
  text-decoration: line-through;
}
.pv-row__delivery-cost { font-size: 0.6875rem; color: var(--m-on-surface-variant); }
.pv-row__shipping-info { font-size: 0.6875rem; color: var(--m-on-surface-variant); display: flex; align-items: center; gap: 4px; }
.pv-row__shipping--free { color: var(--m-success); font-weight: 600; }

.pv-row__shop { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; }
.pv-row__merchant { font-size: 0.8125rem; font-weight: 500; color: var(--m-on-surface); }

.pv-row__actions { display: flex; align-items: center; gap: var(--m-space-2); }
.pv-row__cta {
  display: inline-flex;
  align-items: center;
  gap: var(--m-space-1);
  padding: var(--m-space-2) var(--m-space-4);
  background: var(--m-primary);
  color: var(--m-on-primary);
  border-radius: var(--m-radius);
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: filter 0.15s ease;
}
.pv-row__cta:hover { background: var(--m-primary-dark); }

.pv-row__wishlist-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
}

.pv-row__discount { position: absolute; top: var(--m-space-2); left: var(--m-space-2); }

/* Mobile: Stack vertically */
@media (max-width: 768px) {
  .pv-row {
    grid-template-columns: 60px 1fr;
    grid-template-rows: auto auto;
    gap: var(--m-space-2);
  }
  .pv-row__image { grid-row: 1 / 3; }
  .pv-row__name { grid-column: 2; }
  .pv-row__price { grid-column: 2; flex-direction: row; align-items: center; gap: var(--m-space-2); flex-wrap: wrap; }
  .pv-row__shop { grid-column: 1 / -1; flex-direction: row; align-items: center; gap: var(--m-space-2); }
  .pv-row__actions { grid-column: 1 / -1; }
  .pv-row__cta { flex: 1; justify-content: center; }
  /* Discount: aus dem absoluten Overlay heraus auf Image-Overlay (oben links auf dem Bild) */
  .pv-row__discount { position: absolute; top: 4px; left: 4px; z-index: 1; }
}
</style>
